import React, { useState, useEffect, useRef } from "react";
import ApiTaskScreen from "../../../app/api/admin/task/ApiTaskScreen";
import DateLib from "../../../inc/DateLib";
import { renderOverlay, highlight, KIND_COLORS } from "../../../inc/overlay";
import Modal from "../../components/modals/modal";
import Button from "../../components/button";
import Loader from "../../components/loader";

// Порог уверенности распознавания, ниже которого текст подсвечивается как сомнительный.
// 70 — эмпирический порог ScreenInterpreterService: ниже начинается мусор вроде «<->».
const CONFIDENCE_LOW = 70;

// Лента сканов одного запуска device-скрипта и карточка отдельного скана.
// Два режима в одной модалке: список кадров → выбранный кадр → назад к списку.
// Отдельного роута сознательно нет: сканы существуют только в контексте своего запуска
// (см. докблок DeviceScriptExecuteScreensController), точка входа — строка на «Задачах».
export default function TaskScreensModal({ executeId, label, onClose }) {
    const [_screens, set_screens] = useState([]);
    const [_page, set_page] = useState(1);
    const [_lastPage, set_lastPage] = useState(1);
    const [_isLoad, set_isLoad] = useState(false);
    const [_screenId, set_screenId] = useState(null); // открытая карточка; null — лента

    useEffect(() => {
        let isActual = true;
        (async () => {
            set_isLoad(true);
            const api = ApiTaskScreen.IndexOfExecute(executeId, { page: _page });
            const data = await api.getResponse();
            if (!isActual) return;
            set_isLoad(false);
            if (data) {
                set_screens(data);
                set_lastPage(api.paginationLast ?? 1);
            }
        })();
        return () => { isActual = false; };
    }, [executeId, _page]);

    const title = `Сканы экрана: ${label} (запуск #${executeId})`;

    if (_screenId !== null) {
        return <Modal isOpenModal={true} closeModal={onClose} title={title} className="tasks__screens-modal">
            <TaskScreenCard screenId={_screenId} onBack={() => set_screenId(null)}/>
        </Modal>;
    }

    return <Modal isOpenModal={true} closeModal={onClose} title={title} className="tasks__screens-modal">
        {_isLoad && _screens.length === 0 ? <Loader/> : null}
        {!_isLoad && _screens.length === 0
            ? <div className="tasks__empty">Скрипт не снимал экран на этом запуске</div>
            : null}

        <div className="tasks__screens">
            {_screens.map((screen) => <button
                type="button"
                key={screen.id}
                className="tasks__screen"
                onClick={() => set_screenId(screen.id)}
                title="Открыть скан"
            >
                {screen.screenshot_url
                    ? <img className="tasks__screen-shot" src={screen.screenshot_url}
                        alt={`Скан #${screen.id}`} loading="lazy"/>
                    : <span className="tasks__screen-shot tasks__screen-shot_empty">Кадр не снят</span>}
                <span className="tasks__screen-meta">
                    <span>{DateLib.formatterDateTime(screen.created_at)}</span>
                    {/* null — распознавание не отработало вовсе; 0 — отработало и не нашло ничего. */}
                    <span>{screen.elements_count === null ? 'без распознавания' : `${screen.elements_count} эл.`}</span>
                </span>
            </button>)}
        </div>

        {_lastPage > 1 ? <div className="tasks__screens-pager">
            <Button disabled={_page <= 1 || _isLoad} onClick={() => set_page(_page - 1)}>Назад</Button>
            <span>{_page} / {_lastPage}</span>
            <Button disabled={_page >= _lastPage || _isLoad} onClick={() => set_page(_page + 1)}>Вперёд</Button>
        </div> : null}
    </Modal>;
}

// Карточка скана: кадр с наложенной разметкой click_targets + список распознанного.
//
// Разметку рисует общий с бэкендом рендерер (src/inc/overlay.js — копия tools/overlay.js
// сервиса распознавания), а не своя вёрстка: у него те же цвета и тот же порядок отрисовки,
// что у питоновского overlay.py, и кадр в панели выглядит ровно так же, как при калибровке
// порогов. Заодно он умеет то, чего своя реализация не умела: фигуры (shapes) с цветом по
// виду и сортировкой по убыванию площади — без неё вложенная карточка закрывает кнопку внутри
// себя, — и точки клика.
//
// Рендерер императивный (строит DOM сам), поэтому живёт в useEffect по ref, а не в JSX:
// иначе React и модуль спорили бы за одно поддерево.
function TaskScreenCard({ screenId, onBack }) {
    const [_screen, set_screen] = useState(null);
    const [_activeId, set_activeId] = useState(null); // подсвеченный объект (наведение в списке / клик по кадру)
    const stageRef = useRef(null);
    const svgRef = useRef(null);

    useEffect(() => {
        let isActual = true;
        (async () => {
            const data = await ApiTaskScreen.Show(screenId).getResponse();
            if (isActual) set_screen(data);
        })();
        return () => { isActual = false; };
    }, [screenId]);

    const result = _screen?.result ?? null;
    const elements = result?.elements ?? [];
    const items = result?.items ?? null;
    // image лежит и рядом с result (detailed-ветка резолвера), и внутри самого разбора.
    const imageSize = _screen?.image ?? result?.image ?? null;

    // Пересборка разметки на каждый новый разбор. Без image рендерер работать не может —
    // viewBox брать не из чего, а bbox'ы в пикселях исходного кадра сами по себе бессмысленны.
    useEffect(() => {
        const stage = stageRef.current;
        if (stage === null || result === null || !result.image) return undefined;

        const svg = renderOverlay(result, { onSelect: (object) => set_activeId(object.id) });
        stage.append(svg);
        svgRef.current = svg;

        return () => {
            svg.remove();
            svgRef.current = null;
        };
    }, [result]);

    // Подсветка — отдельным эффектом: меняется от наведения мышью, пересобирать ради неё
    // всю разметку незачем (highlight правит stroke-width уже нарисованных узлов).
    useEffect(() => {
        if (svgRef.current !== null) highlight(svgRef.current, _activeId);
    }, [_activeId]);

    if (_screen === null) return <Loader/>;

    return <div className="tasks__screen-card">
        <div className="tasks__screen-card-head">
            <Button onClick={onBack}>← К ленте</Button>
            <span className="tasks__time">
                Скан #{_screen.id} · {DateLib.formatterDateTime(_screen.created_at)}
                {imageSize ? ` · ${imageSize.width}×${imageSize.height}` : ''}
            </span>
        </div>

        <div className="tasks__screen-card-body">
            {/* stage для overlay.js: позиционированный блок (position:sticky годится так же,
                как relative) + картинка display:block шириной 100%, svg встаёт поверх и
                растягивается по ней (см. .tasks__screen-canvas). */}
            <div className="tasks__screen-canvas" ref={stageRef} style={getCanvasStyle(imageSize)}>
                {_screen.screenshot_url
                    ? <img src={_screen.screenshot_url} alt={`Скан #${_screen.id}`}/>
                    : <div className="tasks__screen-shot_empty">Кадр не снят — есть только распознавание</div>}
            </div>

            <div className="tasks__screen-elements">
                {items ? <ScreenSummary items={items}/> : null}

                <div className="tasks__screen-elements-title">Распознано: {elements.length}</div>
                {elements.length === 0
                    ? <div className="tasks__empty">Ничего не распознано</div>
                    : <table className="tasks__table">
                        <thead>
                            <tr><th>Текст</th><th>Центр</th><th>Уверенность</th></tr>
                        </thead>
                        <tbody>
                            {elements.map((element) => <tr
                                key={element.id}
                                className={_activeId === element.id ? 'tasks__screen-row_active' : ''}
                                onMouseEnter={() => set_activeId(element.id)}
                                onMouseLeave={() => set_activeId(null)}
                            >
                                <td>{element.text}</td>
                                <td>{Array.isArray(element.center) ? element.center.join(', ') : '—'}</td>
                                <td>
                                    <span className={`badge ${element.confidence < CONFIDENCE_LOW ? 'badge_warn' : 'badge_online'}`}>
                                        {element.confidence}
                                    </span>
                                </td>
                            </tr>)}
                        </tbody>
                    </table>}

                {shapesTotal(result) > 0 ? <ScreenShapes result={result} activeId={_activeId}
                    onHover={set_activeId}/> : null}
            </div>
        </div>
    </div>;
}

// Ширина колонки кадра — по пропорциям самого кадра.
//
// Кадр прилипает к верху панели (.tasks__screen-canvas, position: sticky), чтобы не уезжать,
// пока листаешь список элементов. Но липкий блок держится, только если целиком помещается в
// видимую часть скролл-контейнера: что не влезло — прокручивается вместе с содержимым. Кадр
// показывается во всю ширину колонки, поэтому его высота — производная от ширины, и загонять
// его в окно нужно именно шириной.
//
// Вертикальный кадр (телефон) упирается в высоту окна модалки, горизонтальный — в предел
// ширины колонки, за которым списку элементов не остаётся места. Берём меньшее из двух: одна
// формула покрывает обе ориентации. Оба предела заданы в styles.css переменными на модалке,
// здесь — только пропорция кадра.
function getCanvasStyle(imageSize) {
    if (!imageSize?.width || !imageSize?.height) return undefined;

    const ratio = imageSize.width / imageSize.height;
    return { width: `min(var(--screen-canvas-max-width), calc(var(--screen-canvas-height) * ${ratio}))` };
}

// Опись экрана (result.items) — «что вообще на кадре», в отличие от elements/shapes, которые
// говорят «где именно». full_text склеен пробелом, а не переносом: так находится и подпись,
// которую перенос строки разбил на два элемента (см. README click_targets).
function ScreenSummary({ items }) {
    return <div className="tasks__screen-summary">
        <div className="tasks__screen-summary-row">
            <span>Тема: {items.theme === 'dark' ? 'тёмная' : items.theme === 'light' ? 'светлая' : '—'}</span>
            {items.background_color
                ? <span className="tasks__screen-color">
                    <i style={{ background: items.background_color.hex }}/>
                    Фон: {items.background_color.hex} ({items.background_color.name})
                </span>
                : null}
        </div>
        {items.full_text
            ? <div className="tasks__screen-fulltext" title={items.full_text}>{items.full_text}</div>
            : null}
    </div>;
}

// Сколько фигур в разборе всего — по ним решаем, показывать ли блок.
function shapesTotal(result) {
    return (result?.shapes ?? []).length;
}

// Фигуры по видам: цвет кружка — из палитры рендерера, чтобы строка списка и рамка на кадре
// читались как одно и то же. Наведение подсвечивает фигуру на кадре тем же highlight().
function ScreenShapes({ result, activeId, onHover }) {
    const shapes = result.shapes ?? [];

    return <div className="tasks__screen-shapes">
        <div className="tasks__screen-elements-title">Фигуры: {shapes.length}</div>
        <table className="tasks__table">
            <thead>
                <tr><th>Вид</th><th>Текст внутри</th><th>Центр</th></tr>
            </thead>
            <tbody>
                {[...shapes].sort((a, b) => b.area - a.area).map((shape) => <tr
                    key={shape.id}
                    className={activeId === shape.id ? 'tasks__screen-row_active' : ''}
                    onMouseEnter={() => onHover(shape.id)}
                    onMouseLeave={() => onHover(null)}
                >
                    <td>
                        <span className="tasks__screen-color">
                            <i style={{ background: KIND_COLORS[shape.kind] ?? KIND_COLORS.other }}/>
                            {shape.kind}
                        </span>
                    </td>
                    <td>{shape.text || '—'}</td>
                    <td>{Array.isArray(shape.center) ? shape.center.join(', ') : '—'}</td>
                </tr>)}
            </tbody>
        </table>
    </div>;
}
